import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, X } from 'lucide-react';

const AntiFraudModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsOpen(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.lenis?.stop();
      window.addEventListener('keydown', handleKey);
    }
    return () => {
      document.body.style.overflow = '';
      window.lenis?.start();
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid="antifraud-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/70 backdrop-blur-md"
          onClick={() => setIsOpen(false)}
        >
          <motion.div
            data-testid="antifraud-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="antifraud-title"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg bg-[#0a0a0a]/90 backdrop-blur-2xl border border-white/[0.08] rounded-2xl p-8 md:p-10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Gold glow accent */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#FFD700] rounded-full blur-[120px] opacity-[0.07] pointer-events-none"></div>
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent"></div>

            <button
              data-testid="antifraud-close-button"
              onClick={() => setIsOpen(false)}
              aria-label="Close warning"
              className="absolute top-5 right-5 text-gray-500 hover:text-[#FFD700] transition-colors cursor-pointer"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full border border-[#FFD700]/30 bg-[#FFD700]/10 flex items-center justify-center">
                <ShieldAlert size={22} className="text-[#FFD700]" />
              </div>
              <h2
                id="antifraud-title"
                className="text-[#FFD700] font-space-grotesk tracking-widest text-sm md:text-base uppercase font-bold"
              >
                Anti-Fraud Warning
              </h2>
            </div>

            <p className="text-gray-300 text-sm md:text-base font-light leading-relaxed mb-6">
              iMvula will never ask for any form of payment or fees during the recruitment process. Please remain vigilant against recruitment scams. Any request for money from any person purporting to represent iMvula must be reported to{' '}
              <a href="mailto:info@imvula.net" className="text-[#FFD700] hover:underline">info@imvula.net</a>.
            </p>

            <div className="border-t border-white/[0.08] pt-6 mb-8">
              <p className="text-gray-400 text-sm font-light leading-relaxed">
                Job seekers should submit their CV via iMvula's web portal or by email to{' '}
                <a href="mailto:recruitment@imvula.net" className="text-[#FFD700] hover:underline">recruitment@imvula.net</a>
              </p>
            </div>

            <button
              data-testid="antifraud-acknowledge-button"
              onClick={() => setIsOpen(false)}
              className="w-full group/btn relative py-4 bg-[#FFD700] overflow-hidden rounded-sm cursor-pointer"
            >
              <div className="absolute inset-0 bg-white transition-transform duration-500 origin-bottom translate-y-full group-hover/btn:translate-y-0"></div>
              <span className="relative z-10 font-space-grotesk uppercase tracking-wider text-sm font-bold text-black transition-colors duration-500">
                I Understand
              </span>
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AntiFraudModal;
